import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui';
import { agentApi, type BatchCall } from '@/shared/api/agentApi';

/**
 * מצב סבב החיוג: כל מי שחויג, ומה קרה לו.
 *
 * הפאנל מציג בהתחלה את **כל** מי שהסבב חייג אליו, ומצטמצם ככל
 * שמתבררות התוצאות — מי שלא ענה יורד מהרשימה הפעילה ונשאר בתחתית
 * כשורה מעומעמת. בלי זה הנציג לא יודע אם המערכת בכלל ניסתה, וזו בדיוק
 * התלונה "זה לא מתקדם למספר הבא".
 *
 * ה-polling נעצר מעצמו ברגע שאין יותר שיחות מצלצלות — אין טעם לתשאל
 * שרת על סבב שהסתיים.
 */

const STATE_META: Record<BatchCall['state'], { label: string; color: string; icon: string }> = {
  ringing: { label: 'מצלצל', color: 'var(--warn)', icon: 'phoneOut' },
  answered: { label: 'ענה', color: 'var(--ok)', icon: 'ok' },
  no_answer: { label: 'לא ענה', color: 'var(--muted)', icon: 'phoneMiss' },
  abandoned: { label: 'ענה ולא חובר', color: 'var(--danger)', icon: 'phoneMiss' },
};

/** כמה תשאולים ריקים סובלים לפני שמכריזים על סבב שנגמר בלי שיחות. */
const EMPTY_TICKS_BEFORE_SETTLED = 5;

export function DialBatchPanel({ campaignId, batch, onSettled, onCalls }: {
  campaignId: number;
  batch: string | null;
  /** הסבב הוכרע (אף קו לא מצלצל) — נקרא פעם אחת לכל batch. */
  onSettled?: (batch: string, calls: BatchCall[]) => void;
  /** כל תשאול — כדי שההורה יציג את הלידים שחויגו בלי poller משלו. */
  onCalls?: (calls: BatchCall[]) => void;
}) {
  const [calls, setCalls] = useState<BatchCall[]>([]);
  // ה-callbacks דרך ref: ההורה מייצר פונקציות חדשות בכל רינדור, ואסור
  // שזה יאתחל את התשאול (ויירה onSettled פעמיים).
  const onSettledRef = useRef(onSettled);
  const onCallsRef = useRef(onCalls);
  onSettledRef.current = onSettled;
  onCallsRef.current = onCalls;
  const settledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!batch) { setCalls([]); return; }

    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let emptyTicks = 0;

    const settle = (rows: BatchCall[]) => {
      if (settledRef.current === batch) return;
      settledRef.current = batch;
      onSettledRef.current?.(batch, rows);
    };

    const tick = async () => {
      try {
        const rows = await agentApi.dialerBatchStatus(campaignId, batch);
        if (!alive) return;
        setCalls(rows);
        onCallsRef.current?.(rows);
        // שורות ה-Call נכתבות רגע אחרי ה-originate — סבב שנראה ריק בתשאול
        // הראשון עדיין לא הוכרע. רק אחרי כמה תשאולים ריקים מוותרים.
        if (rows.length === 0) {
          emptyTicks += 1;
          if (emptyTicks < EMPTY_TICKS_BEFORE_SETTLED) { timer = setTimeout(tick, 2000); return; }
          settle(rows);
          return;
        }
        // עוד מצלצל ⇒ ממשיכים; אחרת הסבב הוכרע ואין מה לתשאל.
        if (rows.some((r) => r.state === 'ringing')) {
          timer = setTimeout(tick, 2000);
        } else {
          settle(rows);
        }
      } catch {
        // כשל רשת אינו מפיל את המסך — פשוט מפסיקים לתשאל.
      }
    };

    void tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [campaignId, batch]);

  if (!batch || calls.length === 0) return null;

  const live = calls.filter((c) => c.state === 'ringing' || c.state === 'answered');
  const dropped = calls.filter((c) => c.state === 'no_answer' || c.state === 'abandoned');

  return (
    <div style={{
      border: '1px solid var(--line)', borderRadius: 'var(--r-3)',
      padding: 'var(--pad-card)', marginBottom: 'var(--gap)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>סבב חיוג</strong>
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          {calls.length} חויגו · {calls.filter((c) => c.state === 'answered').length} ענו
        </span>
      </div>

      <div className="stack" style={{ gap: 4 }}>
        {live.map((c) => (
          <Row key={c.call_id} call={c} />
        ))}
        {dropped.length > 0 && (
          <>
            {live.length > 0 && <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />}
            {dropped.map((c) => (
              <Row key={c.call_id} call={c} dimmed />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ call, dimmed = false }: { call: BatchCall; dimmed?: boolean }) {
  const meta = STATE_META[call.state];

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 8px', borderRadius: 'var(--r-2)',
      background: call.state === 'answered' ? 'var(--ok-soft)' : 'transparent',
      opacity: dimmed ? 0.55 : 1,
    }}>
      <Icon name={meta.icon} size={14} color={meta.color} />
      <span style={{ flex: 1, fontSize: 'var(--t-sm)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {call.name?.trim() || 'ללא שם'}
      </span>
      <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)' }}>{call.phone}</span>
      <span style={{ fontSize: 'var(--t-xs)', color: meta.color, fontWeight: 600, minWidth: 46, textAlign: 'end' }}>
        {meta.label}
      </span>
    </div>
  );
}
